import React from "react";
import { motion } from "framer-motion";
import { Phone, Mail, Sparkles, BookOpen, Star, ArrowRight } from "lucide-react";
import { ASSETS, CONTACT_INFO, PRICING } from "@/data/site";

const scrollTo = (id) => () => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const fadeUp = { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 } };

const Hero = () => {
  return (
    <section id="home" className="section-light relative min-h-screen pt-24" data-testid="hero-section">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 relative z-10">
        <div className="flex flex-col items-center text-center min-h-[calc(100vh-7rem)] py-16 justify-center">

          <motion.img
            src={ASSETS.tarotHorizontal}
            alt="Tarot cards spread"
            className="w-72 md:w-96 mb-8"
            style={{ filter: "drop-shadow(0 12px 28px rgba(19,26,72,0.18))" }}
            {...fadeUp}
            transition={{ delay: 0.1, duration: 0.8 }}
            data-testid="hero-cards-image"
          />

          <motion.div
            {...fadeUp}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase mb-6"
            style={{ color: "var(--cs-purple)" }}
          >
            <Star className="w-3.5 h-3.5 fill-current" /> 35 Years of Intuitive Practice
          </motion.div>

          <motion.h1
            {...fadeUp}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="font-serif-display text-6xl sm:text-7xl lg:text-8xl leading-[1.02] tracking-tight"
            data-testid="hero-heading"
          >
            <span className="toroidal-text">Verna Carr</span>
            <span className="block toroidal-text italic font-medium mt-3">Tarot Card Readings</span>
          </motion.h1>

          <motion.p
            {...fadeUp}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="mt-8 text-xl md:text-2xl leading-relaxed max-w-3xl"
            style={{ color: "#334155" }}
            data-testid="hero-subheading"
          >
            Whether you seek clarity in love, career, or spiritual growth — a personal tarot reading offers wisdom and guidance.
          </motion.p>

          <motion.div
            {...fadeUp}
            transition={{ delay: 0.55, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-4 justify-center"
          >
            <button onClick={scrollTo("contact")} className="btn-pill-primary text-base" data-testid="hero-cta-book">
              <Sparkles className="w-4 h-4" /> Book a personal session
            </button>
            <button onClick={scrollTo("tarot")} className="btn-pill-blue text-base" data-testid="hero-cta-learn">
              <BookOpen className="w-4 h-4" /> Learn the tarot
            </button>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-14 flex flex-col sm:flex-row gap-5 sm:gap-10 text-base"
          >
            <a href={CONTACT_INFO.phoneHref} className="flex items-center gap-3 transition" style={{ color: "#334155" }} data-testid="hero-phone">
              <span className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "rgba(153,76,153,0.10)" }}>
                <Phone className="w-5 h-5" style={{ color: "var(--cs-purple)" }} />
              </span>
              <span>Verna — <span style={{ color: "var(--cs-navy)" }} className="font-medium">{CONTACT_INFO.phone}</span></span>
            </a>
            <a href={CONTACT_INFO.emailHref} className="flex items-center gap-3 transition" style={{ color: "#334155" }} data-testid="hero-email">
              <span className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "rgba(153,76,153,0.10)" }}>
                <Mail className="w-5 h-5" style={{ color: "var(--cs-purple)" }} />
              </span>
              <span className="break-all" style={{ color: "var(--cs-navy)" }}>{CONTACT_INFO.email}</span>
            </a>
          </motion.div>
        </div>
      </div>

      {/* PRICING */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{ color: "var(--cs-purple)" }}>Sessions</p>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-tight" style={{ color: "var(--cs-blue-dk)" }}>
            Choose your <span className="italic" style={{ color: "var(--cs-purple)" }}>reading</span>
          </h2>
          <p className="mt-4" style={{ color: "#475569" }}>In person in Whitianga, or by phone & online — anywhere in the world.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {PRICING.map((p) => (
            <div
              key={p.id}
              className="relative rounded-3xl p-8 transition-all hover:-translate-y-1"
              style={{
                background: "#fff",
                border: `1px solid ${p.popular ? "rgba(153,76,153,0.45)" : "rgba(19,26,72,0.10)"}`,
                boxShadow: p.popular
                  ? "0 12px 40px rgba(153,76,153,0.20)"
                  : "0 6px 20px rgba(19,26,72,0.06)",
              }}
              data-testid={`pricing-card-${p.id}`}
            >
              {p.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-white text-xs uppercase tracking-widest px-4 py-1 rounded-full font-medium whitespace-nowrap"
                      style={{ background: "linear-gradient(135deg,#994c99,#d2928c)" }}>
                  All of your questions answered
                </span>
              )}
              <p className="text-sm uppercase tracking-[0.2em] mb-3" style={{ color: "#64748B" }}>{p.duration}</p>
              <p className="font-serif-display text-5xl mb-3" style={{ color: "var(--cs-navy)" }}>{p.price}</p>
              <p className="leading-relaxed text-sm" style={{ color: "#475569" }}>{p.desc}</p>
              <button
                onClick={scrollTo("contact")}
                className="mt-6 inline-flex items-center gap-2 text-sm transition font-medium"
                style={{ color: "var(--cs-purple)" }}
                data-testid={`pricing-cta-${p.id}`}
              >
                Book this session <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
